import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { RoofColorTagOptionView, SetManualRoofColorBody } from "@slfinrtl/ui-bff-routes"
import { getRoofColorTagOptionsRoute, setManualRoofColorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import type { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { propertiesOf } from "src/common/property"
import { getMarketLanguageTranslations } from "src/common/translations"
import { useBff, useBffDynamic } from "src/hooks"
import type { CustomRoofColorContainerProps } from "./CustomRoofColorContainer"
import type { CustomRoofColorProps } from "./view/CustomRoofColor"

export const messages = defineMessages({
	hexCode: {
		defaultMessage: "Color Shade",
		id: "colors.roofColor.customRoofColorModal.hexCode",
	},
	name: {
		defaultMessage: "Color Name",
		id: "colors.roofColor.customRoofColorModal.name",
	},
	selectCatalogRoofColor: {
		defaultMessage: "Select Catalog Roof Color",
		id: "colors.roofColor.customRoofColorModal.selectCatalogRoofColor",
	},
	tag: {
		defaultMessage: "Color Group",
		id: "colors.roofColor.customRoofColorModal.tag",
	},
})

export interface CustomRoofColorForm {
	hexCode: string
	name: Record<string, string>
	tagKey: string
}

export const useCustomRoofColor = ({
	currentRoofColor,
	inventoryId,
	onRoofColorCreated,
	showCatalogRoofColorContainer,
}: CustomRoofColorContainerProps): {
	form: UseFormReturn<CustomRoofColorForm>
	onSubmit: SubmitHandler<CustomRoofColorForm>
	props: CustomRoofColorProps | undefined
} => {
	const { availableLocales } = useDealerContext()

	const [tagOptions, setTagOptions] = useState<RoofColorTagOptionView[]>()

	const getCustomRoofColorOptions = useBff(
		getRoofColorTagOptionsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {},
		},
		"inline",
	)

	useEffect(() => {
		getCustomRoofColorOptions().then(setTagOptions)
	}, [getCustomRoofColorOptions])

	const form = useForm<CustomRoofColorForm>({
		defaultValues: {
			hexCode: currentRoofColor?.hexCode ?? "",
			name: getMarketLanguageTranslations(availableLocales, currentRoofColor?.name ?? {}),
			tagKey: currentRoofColor?.tagKey ?? "",
		},
	})

	const setManualRoofColor = useBffDynamic(setManualRoofColorRoute, "inline")

	const [hasSubmitFailed, setHasSubmitFailed] = useState(false)
	const onSubmit: SubmitHandler<CustomRoofColorForm> = async (formState) => {
		const setManualRoofColorBody: SetManualRoofColorBody = {
			hexCode: formState.hexCode,
			name: getMarketLanguageTranslations(availableLocales, formState.name),
			tagKey: formState.tagKey,
		}

		await setManualRoofColor({
			body: setManualRoofColorBody,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		})
			.then(() =>
				onRoofColorCreated({
					optionCode: null,
					...setManualRoofColorBody,
				}),
			)
			.catch(() => setHasSubmitFailed(true))
	}

	const { formatDisplayName, formatMessage } = useIntl()

	return {
		form,
		onSubmit,
		props: tagOptions && {
			hasSubmitFailed,
			hexCode: {
				label: formatMessage(messages.hexCode),
				name: customRoofColorFormProperties.hexCode._path,
				required: true,
			},
			isSubmitting: form.formState.isSubmitting,
			name: availableLocales.map((languageTag) => ({
				label: `${formatMessage(messages.name)} - ${formatDisplayName(languageTag, {
					type: "language",
				})}`,
				name: `${customRoofColorFormProperties.name._path}.${languageTag}`,
				required: true,
				type: "text",
				validate: (value: string | number) =>
					value.toString() !== value.toString().toUpperCase() ||
					formatMessage(commonMessages.onlyCapitalLetters),
			})),
			setHasSubmitFailed,
			showCatalogRoofColorContainer,
			tag: {
				label: formatMessage(messages.tag),
				name: customRoofColorFormProperties.tagKey._path,
				options: tagOptions.map((tag) => ({
					hexCode: tag.hexCode,
					label: tag.name,
					value: tag.key,
				})),
				required: true,
			},
		},
	}
}

const customRoofColorFormProperties = propertiesOf<CustomRoofColorForm>()
